import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onModuleRegistration } from '../../../loader/decorators/on-module-registration.js';
import { ID_STORE, STORAGE_HELPER } from '../../token.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';
import { IdStore } from './idstore.js';

declare const window: Window & yieldlove.Window;

const win = window;
win.pbjsYLHH = win.pbjsYLHH || {};
win.pbjsYLHH.que = win.pbjsYLHH.que || [];

interface RecoverId {
    name: string;
    key?: string;
    decode?: (raw: string) => string;
}

const identity = <T>(v: T) => v;

/**
 * Pull ids from localStorage that were set beforehand by Prebid.
 * Ids are created normally after the first auction from Prebid, which happens way after this script.
 * It's very likely that PreBid Ids will only be available on return visits of a user.
 */
@injectionTarget()
export class RecoverPrebidIds {

    @inject(STORAGE_HELPER) #storageHelper!: StorageHelper;
    @inject(ID_STORE) #idStore!: IdStore;

    constructor() {
        this.recoverPrebidIdsAfterRegistration();
    }

    /**
     * Gather ids, that can directly be read from localStorage.
     * as soon as local storage consent is available
     */
    @onModuleRegistration()
    public recoverPrebidIds(): void {
        const recoverIds: RecoverId[] = [
            { name: 'sharedid' },
            { name: 'criteo', key: 'cto_bidid' },
            { name: 'pubCommonId', key: '_pubcid' },
            { name: 'utiq_mtid', key: 'utiqPass', decode: raw => JSON.parse(raw)?.connectId?.idGraph?.[0]?.mtid || '' },
            { name: 'utiq', key: 'utiqPass', decode: raw => JSON.parse(raw)?.connectId?.idGraph?.[0]?.atid || '' },
        ];

        for (const { name, key = name, decode = identity } of recoverIds) {
            this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, key).then(value => {
                value && this.#idStore.setIdPromise(name, decode(value));
            });
        }

        // special solution for id5id, for saving linktype in addition to the id
        this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, 'id5id').then(entry => {
            if (entry) {
                const id5AsJson = JSON.parse(decodeURIComponent(entry));
                this.#idStore.setIdPromise('id5id', id5AsJson.universal_uid || '', { linkType: id5AsJson.ext?.linkType || '' });
            }
        });
    }

    /**
     * waits for prebid to generate user Ids and
     * recovers the user Ids from localStorage
     */
    private recoverPrebidIdsAfterRegistration(): void {
        win.pbjsYLHH.que.push(() => {
            win.pbjsYLHH.getUserIdsAsync().then(() => {
                this.recoverPrebidIds();
            });
        });
    }
}
